import { Form } from "antd"
import theme from "utils/themeVars"
import {
  Divider,
  Group,
  InputNumber,
  Modal,
  SelectInput,
  Spinner,
  Text,
  notification,
  Button,
  Space,
  TextInput,
  Icon,
  Checkbox,
} from "components/common"
import MonthPicker from "components/common/monthPicker/monthPicker"
import { IconTextDate } from "components/custom"
import {
  useAddSalesPipelineMutation,
  useDeleteSalesPipelineMutation,
  useLazyGetInternalInquiryStagesQuery,
  useUpdateSalesPipelineMutation,
} from "features/salesPipeline/api"
import { useLazyGetProjectsQuery, useLazyGetUnitsByProjectQuery } from "features/shared/api"
import { classifications } from "features/shared/data"
import { schemaRules } from "features/shared/utils"
import dayjs from "dayjs"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { localisedValue } from "utils"
import { dateTimeFunction } from "utils/date"
import addToSalesPipelineSchemaRules from "./addToSalesPipelineSchemRules"
import getPrivilege from "utils/privileges/privileges"
import { useSelector } from "react-redux"
import Flex from "components/common/flex"
import { useQueryUpdate } from "hooks"
import ClientProfileForm from "../../../tasks/components/clientProfileForm/clientProfileForm"
import { useLazyGetClientAnswerQuery } from "../../../tasks/api"
import { useWatch } from "antd/lib/form/Form"
import ConfirmationModal from "../confirmationModal/confirmationModal"

const getPrimaryProjectId = e => e?.interestedIn === "primary" && e?.Project?.projectId?.toString()

const DISABLED_STAGES = { preclosure: true, partialSold: true, sold: true }

const AddToSalesPipeline = forwardRef(({ client, allocatedTo, lead, isFromUpdate, onAddPipeline = () => {} }, ref) => {
  const [update] = useQueryUpdate()
  const [shouldPrefill, setShouldPrefill] = useState()
  const clientProfileRef = useRef()
  const { t } = useTranslation()
  const [getProjects, { data: projects, isFetching: isFetchingProjects, isLoading: loadingProjects }] =
    useLazyGetProjectsQuery()
  const [getUnitNumber, { data: unitsProject, isFetching: fetchUnits, isLoading: loadUnits }] =
    useLazyGetUnitsByProjectQuery()
  const [form] = Form.useForm()
  const [getInternalStatus, { data: inquiryStages, isFetching: inquiryStagesFetching, isLoading: loadStages }] =
    useLazyGetInternalInquiryStagesQuery()
  const [addSalesPipeline, { isLoading }] = useAddSalesPipelineMutation()
  const [updateSalesPipeline, { isLoading: isUpdating, reset }] = useUpdateSalesPipelineMutation()
  const [deleteSalesPipeline, { isLoading: isDeleting }] = useDeleteSalesPipelineMutation()
  const [getClientAnswers, { data: answerData, isFetching: isFetchingClientAnswers }] = useLazyGetClientAnswerQuery()
  const rules = schemaRules(addToSalesPipelineSchemaRules())
  const isLeadLocked = useWatch("isLeadLocked", form)
  const userPrivileges = useSelector(state => state.app.privileges)
  const removeLeadRef = useRef()
  const onModalVisible = () => {
    getProjects(
      {
        ...(!isFromUpdate && {
          skipPpaExpired: true,
          withSaleableListings: true,
          status: "on",
        }),
        financials: true,
        paymentInfo: true,
      },
      true
    )
      .unwrap()
      .then(projectList => {
        setShouldPrefill(
          projectList?.filter(project =>
            isFromUpdate ? project?.projectId == lead?.projectId : project?.projectId == lead?.Project?.projectId
          )?.length
        )
      })
    getInternalStatus(null, true)
    getInitialValues()
  }

  const getInitialValues = useCallback(() => {
    form.setFieldsValue({
      projectId: shouldPrefill ? (isFromUpdate ? lead?.projectId?.toString() : getPrimaryProjectId(lead)) : null,
      unit: shouldPrefill ? lead?.unitId?.toString() || lead?.listingId?.toString() || null : null,
      probability: lead?.probability || lead?.percentage || lead?.Client?.aiClosureProbability || 1,
      stage: lead?.leadStageId || lead?.inquiryStageId || +inquiryStages?.[0]?.value,
      startDate: lead?.date ? dateTimeFunction({ date: lead?.date, timeZone: true }) : dayjs(),
      comment: lead?.comment,
      isLeadLocked: lead?.isManuallyLocked === 1,
    })
  }, [lead, isFromUpdate, form, inquiryStages, shouldPrefill])

  const getLeadStageKey = leadStageId => {
    const leadStage = inquiryStages?.find(item => item.value === leadStageId)
    return leadStage?.key
  }
  useEffect(() => {
    const projectId = shouldPrefill ? (isFromUpdate ? lead?.projectId?.toString() : getPrimaryProjectId(lead)) : null
    if (projectId) {
      getUnitNumber({ projectIds: projectId }, true)
    }
    getInitialValues(inquiryStages)
  }, [lead, isFromUpdate, inquiryStages, shouldPrefill])

  const onSubmit = e => {
    const values = form.getFieldsValue()
    const startDate = dateTimeFunction({ date: values?.startDate, timeZone: true, apiRequest: true })
    const payload = {
      projectId: values?.projectId,
      listingId: values?.unit,
      inquiryId: isFromUpdate ? lead?.inquiryId : lead?.id,
      conversionProbability: values?.probability,
      inquiryStageId: values?.stage,
      date: startDate,
      comment: values?.comment,
      isManuallyLocked: values?.isLeadLocked ?? false,
    }
    isFromUpdate
      ? updateSalesPipeline({ pipelineId: lead?.id, ...payload })
          .unwrap()
          .then(() => {
            ref?.current?.setVisible(false)
            notification.success({ message: t("Updated To Sales Pipeline") })
            form.resetFields()
            onAddPipeline()
          })
          .catch(error => {
            notification.error({ message: error?.message })
          })
      : addSalesPipeline(payload)
          .unwrap()
          .then(() => {
            ref?.current?.setVisible(false)
            notification.success({ message: t("Added To Sales Pipeline") })
            form.resetFields()
            onAddPipeline()
          })
          .catch(error => {
            notification.error({ message: error?.message })
          })
  }

  const deleteFromPipeline = () => {
    deleteSalesPipeline({ pipelineId: lead?.id })
      .unwrap()
      .then(() => {
        ref?.current?.setVisible(false)
        notification.success({ message: t("Deleted From Sales Pipeline") })
      })
      .catch(error => {
        notification.error({ message: error?.message })
      })
  }

  return (
    <Modal
      width={800}
      title={isFromUpdate ? t("Update Lead In Pipeline") : t("Add Lead To Pipeline")}
      ref={ref}
      onCancel={() => {
        ref?.current?.setVisible(false)
        form.resetFields()
        setShouldPrefill(null)
        update("getUnitsByProject", draft => null)
      }}
      destroyOnClose
      okText={t("Update")}
      cancelText={t("Cancel")}
      onOk={form.submit}
      onModalVisible={onModalVisible}
      loading={isLoading || isUpdating || fetchUnits || isDeleting || isFetchingClientAnswers}
      footer={
        isFromUpdate ? (
          <Space className="w-100" align="center" style={{ justifyContent: "space-between" }}>
            <Button
              size="large"
              key="delete"
              type="primary"
              danger={true}
              loading={isDeleting}
              disabled={
                !getPrivilege("can_delete_from_pipeline", userPrivileges) ||
                ["pre_closure", "sold"].includes(
                  getLeadStageKey(lead?.leadStageId || lead?.inquiryStageId || +inquiryStages?.[0]?.value)
                )
              }
              onClick={() => {
                removeLeadRef.current.setVisible(true)
              }}
              text={t("Remove")}
            />
            <Flex style={{ gap: "8px" }}>
              <Button
                size="large"
                key="cancel"
                type="default"
                onClick={() => {
                  ref?.current?.setVisible(false)
                  form.resetFields()
                }}
                text={t("Cancel")}
              />
              <Button
                size="large"
                key="submit"
                type="primary"
                loading={isUpdating || isFetchingClientAnswers}
                onClick={() => {
                  form.submit()
                }}
                text={t("Update")}
              />
            </Flex>
          </Space>
        ) : (
          false
        )
      }
    >
      {loadingProjects || loadStages ? (
        <Spinner inDisabledLayout />
      ) : (
        <>
          <Form
            form={form}
            onFinish={e => {
              getClientAnswers(client?.id)
                .unwrap()
                .then(e => {
                  if (!e?.profileFilled) {
                    clientProfileRef?.current.setVisible(true)
                  } else {
                    reset()
                    onSubmit(e)
                  }
                })
            }}
            layout="vertical"
            preserve={false}
          >
            <Text className="bold fs16 mbe-8" block>
              {t("Interested Project / Unit")}
            </Text>
            <Group template="repeat(2, 1fr)" gap="8px">
              <Form.Item name={"projectId"} label={t("Project")} required rules={rules}>
                <SelectInput
                  placeholder={t("Select Project")}
                  options={projects}
                  inputLoading={isFetchingProjects}
                  disabled={isFromUpdate && DISABLED_STAGES[lead?.stage]}
                  onChange={value => {
                    form.setFieldsValue({ unit: null })
                    getUnitNumber({ projectIds: value }, true)
                  }}
                />
              </Form.Item>
              <Form.Item name={"unit"} label={t("Unit")} required rules={rules}>
                <SelectInput
                  placeholder={t("Select Unit")}
                  options={unitsProject}
                  inputLoading={fetchUnits}
                  disabled={isFromUpdate && DISABLED_STAGES[lead?.stage]}
                />
              </Form.Item>
            </Group>

            <Text className="bold fs16 mbe-8" block>
              {t("Conversion")}
            </Text>
            <Divider className="m-0 mbe-16" />

            <Group template="repeat(2, 1fr)" gap="8px">
              <Group template="repeat(2, 1fr)" gap="4px">
                <Form.Item
                  name={"probability"}
                  label={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${t("Probability")} (1% - 100%)`}
                      <Icon
                        icon="BsInfo"
                        size="16px"
                        color={theme["gray900"]}
                        hasBackground
                        iconContainerStyle={{ padding: 0 }}
                        tooltipText={t(
                          "Enter the likelihood (in percentage) that this deal will successfully close in the chosen stage during the selected month"
                        )}
                      />
                    </span>
                  }
                  required
                  rules={rules}
                >
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    addonAfter={
                      lead?.aiPercentage != null ? (
                        <span style={{ color: theme["gray700"] }}>AI CP: {lead.aiPercentage}%</span>
                      ) : null
                    }
                  />
                </Form.Item>
                <Form.Item name={"stage"} label={<></>} rules={rules}>
                  <SelectInput
                    options={inquiryStages?.map(stage => ({
                      ...stage,
                      ...(lead?.stage === "sold" && stage?.key === "partial_sold" && { disabled: true }),
                    }))}
                    placeholder={t("Select Stage")}
                  />
                </Form.Item>
              </Group>
              {isFromUpdate && (
                <Form.Item label={t("Comment")} name="comment" required rules={rules}>
                  <TextInput placeholder={t("Please add comment")} />
                </Form.Item>
              )}
              <Form.Item name="startDate" label={t("Closing Month")} rules={rules} required>
                <MonthPicker
                  placeholder={t("Select start date")}
                  format="MMM YYYY"
                  allowClear={false}
                  showTime={false}
                  containerWidth="100%"
                  disabled={lead?.recognizedRevenue > 0 || !!isLeadLocked}
                  value={form.getFieldValue("startDate")}
                  onChange={value => form.setFieldsValue({ startDate: value })}
                />
              </Form.Item>
            </Group>
            <Form.Item name="isLeadLocked" valuePropName="checked">
              <Checkbox
                value={isLeadLocked}
                onChange={e => {
                  form.setFieldsValue({ isLeadLocked: e?.target?.checked })
                }}
                disabled={!getPrivilege("can_lock_lead_in_pipeline", userPrivileges)}
              >
                <>
                  <Text className="gray-900">{t("Lock lead in pipeline")}</Text>
                  <Icon
                    icon="BsInfo"
                    size="16px"
                    color={theme["gray900"]}
                    hasBackground
                    iconContainerStyle={{ padding: 0, marginLeft: 3 }}
                    tooltipText={t(
                      "Lock this lead in the current month’s pipeline and exclude it from monthly rollover"
                    )}
                  />
                </>
              </Checkbox>
            </Form.Item>

            <Divider className="m-0 mbe-16" />
            <IconTextDate
              iconProps={{
                color:
                  classifications(t)[
                    lead?.classificationId || lead?.inquiryClassificationId || lead?.InternalInquiryClassification?.id
                  ]?.style.color,
                hasBackground: true,
              }}
              icon="MdLocalFireDepartment"
              title={isFromUpdate ? lead?.leadId : lead?.id}
            />
            {" - "}
            <Text className="fs16">
              {isFromUpdate
                ? localisedValue({ object: lead, key: "clientName" })
                : localisedValue({ object: lead?.Client, key: "name" })}
            </Text>
            <div>
              <Text className="gray-800 fs14" block>
                {t("Allocated To")}
                {": "}{" "}
                {isFromUpdate
                  ? localisedValue({ key: "userName", object: lead })
                  : localisedValue({ key: "name", object: lead?.User })}
                {`(${localisedValue({
                  key: isFromUpdate ? "userDesignationShort" : "short",
                  object: isFromUpdate ? lead : lead?.User?.UserDesignation,
                })})`}
              </Text>
            </div>
            <Divider className="m-0 mbe-16" />
          </Form>
          <ClientProfileForm
            formRef={clientProfileRef}
            onSuccess={onSubmit}
            client={client}
            allocatedTo={allocatedTo}
          />
          <ConfirmationModal
            ref={removeLeadRef}
            title={t("Remove Lead From Pipeline")}
            onOk={() => {
              removeLeadRef.current.setVisible(false)
              deleteFromPipeline()
            }}
            confirmationText={t("Are you sure you want to remove this lead from pipeline?")}
          />
        </>
      )}
    </Modal>
  )
})

export default AddToSalesPipeline
